/**
 * Notifications AGORA (canal « app ») – données communes au dock (cloche, 10 dernières) et à l'écran
 * Mon profil › Notifications (liste paginée). Contrat : packages/shared/src/schemas-notifications.ts.
 *
 *   GET  /v1/notifications?etat=toutes|non-lues&limite=1..50&suivant=<curseur>  → PageNotifications (+ nonLues)
 *   POST /v1/notifications/{id}/lue                                           → CompteurNotifications
 *   POST /v1/notifications/lues  (tout marquer comme lu)                      → CompteurNotifications
 *
 * TanStack Query, clés sous ['notifications'] : ['notifications', 'dock'] (10 dernières, rafraîchie toutes les 60 s
 * uniquement quand l'onglet est visible, et au retour sur l'onglet) ; ['notifications', 'liste', etat] (pages de 20,
 * curseur `suivant`). Les actions « lue » / « tout lire » mettent à jour TOUS ces caches de façon optimiste (état lu,
 * compteur nonLues), reprennent le compteur renvoyé par l'API, puis invalident ['notifications'] ; retour arrière en
 * cas d'erreur.
 *
 * Volontairement sans dépendance vers web/src/socle (importé par shell/page.ts, lui-même importé par le socle).
 */
import React from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import {
  LIMITE_NOTIFICATIONS_DOCK, TYPES_NOTIFICATION, cleLibre, estTypeNotification,
  type CompteurNotifications, type Notification, type PageNotifications,
} from '@agora/shared';
import type { IconName } from '../../design';
import type { Traduire } from '../i18n';
import { useSocle } from '../contexte';

/** Écran Mon profil › Notifications (route de page). */
export const ROUTE_NOTIFICATIONS = '/profil/notifications';
/** Taille d'une page de l'écran Notifications. */
export const NOTIFICATIONS_PAR_PAGE = 20;
/** Rafraîchissement de la cloche du dock (onglet visible uniquement). */
const INTERVALLE_DOCK = 60_000;

export type FiltreNotifications = 'toutes' | 'non-lues';

const CLE: QueryKey = ['notifications'];
const CHEMIN = '/v1/notifications';

/** Message traduit : `notifications.types.<type>` avec ses variables ; type inconnu (retiré du catalogue) → message générique. */
export function texteNotification(n: Notification, t: Traduire): string {
  if (!estTypeNotification(n.type)) return t('notifications.inconnue');
  // Variable attendue mais absente : tiret plutôt que « {titre} » brut.
  const attendues = Object.fromEntries(TYPES_NOTIFICATION[n.type].variables.map((v) => [v, '—']));
  return t(cleLibre('notifications.types', n.type), { ...attendues, ...n.variables, auteur: n.auteur ?? t('notifications.systeme') });
}

/** Icône du type de notification (cloche pour un type inconnu). */
export function iconeNotification(n: Notification): IconName {
  return estTypeNotification(n.type) ? TYPES_NOTIFICATION[n.type].icone : 'bell';
}

/** Dock : 10 dernières notifications (lues et non lues) et nombre total de non lues (pastille). */
export function useNotificationsDock() {
  const { api } = useSocle();
  return useQuery({
    queryKey: [...CLE, 'dock'],
    queryFn: ({ signal }) => api.get<PageNotifications>(CHEMIN, { etat: 'toutes', limite: LIMITE_NOTIFICATIONS_DOCK }, signal),
    refetchInterval: INTERVALLE_DOCK,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/** Écran Notifications : pages de 20 (curseur `suivant`), filtre toutes / non lues. */
export function useListeNotifications(etat: FiltreNotifications) {
  const { api } = useSocle();
  return useInfiniteQuery({
    queryKey: [...CLE, 'liste', etat],
    queryFn: ({ pageParam, signal }) => api.get<PageNotifications>(CHEMIN, { etat, limite: NOTIFICATIONS_PAR_PAGE, suivant: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (derniere) => derniere.suivant,
  });
}

type Cache = PageNotifications | InfiniteData<PageNotifications, string | undefined>;
type Instantane = [QueryKey, Cache | undefined][];

/** Applique une transformation à une page, que le cache soit une requête simple (dock) ou paginée (écran). */
function transformer(d: Cache | undefined, f: (p: PageNotifications) => PageNotifications): Cache | undefined {
  if (!d) return d;
  return 'pages' in d ? { ...d, pages: d.pages.map(f) } : f(d);
}

/** Évite deux appels pour la même notification (ex. pointeur relâché puis clic sur le même lien). */
const lecturesEnCours = new Set<string>();

/**
 * Actions : `marquerLue(n)` (sans effet si déjà lue) et `toutLire()`, avec mise à jour optimiste de tous les caches.
 * `erreur` : dernière action en échec (null sinon), effacée à l'action suivante.
 */
export function useActionsNotifications() {
  const { api } = useSocle();
  const qc = useQueryClient();

  const appliquer = React.useCallback((f: (p: PageNotifications) => PageNotifications) => {
    qc.setQueriesData<Cache>({ queryKey: CLE }, (d) => transformer(d, f));
  }, [qc]);
  const optimiste = React.useCallback(async (f: (p: PageNotifications) => PageNotifications): Promise<Instantane> => {
    await qc.cancelQueries({ queryKey: CLE });
    const avant = qc.getQueriesData<Cache>({ queryKey: CLE });
    appliquer(f);
    return avant;
  }, [qc, appliquer]);
  const restaurer = (avant?: Instantane) => avant?.forEach(([cle, d]) => qc.setQueryData(cle, d));
  const compteur = (c: CompteurNotifications) => appliquer((p) => ({ ...p, nonLues: c.nonLues }));
  const invalider = () => qc.invalidateQueries({ queryKey: CLE });

  const lue = useMutation({
    mutationFn: (n: Notification) => api.post<CompteurNotifications>(`${CHEMIN}/${encodeURIComponent(n.id)}/lue`, {}),
    onMutate: (n) => {
      const le = new Date().toISOString();
      return optimiste((p) => ({
        ...p,
        elements: p.elements.map((x) => (x.id === n.id && !x.lueLe ? { ...x, lueLe: le } : x)),
        nonLues: Math.max(0, p.nonLues - 1),
      }));
    },
    onError: (_e, _n, avant) => restaurer(avant),
    onSuccess: compteur,
    onSettled: (_r, _e, n) => { lecturesEnCours.delete(n.id); return invalider(); },
  });

  const tout = useMutation({
    mutationFn: () => api.post<CompteurNotifications>(`${CHEMIN}/lues`, {}),
    onMutate: () => {
      const le = new Date().toISOString();
      return optimiste((p) => ({ ...p, elements: p.elements.map((x) => (x.lueLe ? x : { ...x, lueLe: le })), nonLues: 0 }));
    },
    onError: (_e, _v, avant) => restaurer(avant),
    onSuccess: compteur,
    onSettled: invalider,
  });

  const { mutate: lire, reset: effacerLue } = lue;
  const { mutate: toutMarquer, reset: effacerTout } = tout;
  const marquerLue = React.useCallback((n: Notification) => {
    if (n.lueLe || lecturesEnCours.has(n.id)) return;
    lecturesEnCours.add(n.id);
    effacerTout();
    lire(n);
  }, [lire, effacerTout]);
  const toutLire = React.useCallback(() => { effacerLue(); toutMarquer(); }, [toutMarquer, effacerLue]);

  return { marquerLue, toutLire, toutEnCours: tout.isPending, erreur: lue.error ?? tout.error };
}
